/**
 * immerHistory — Zustand 미들웨어: immer produceWithPatches 기반 undo/redo 스택.
 *
 * v2(md-label-maker.next-studio)의 lib/immer-history.ts를 v1에 포팅.
 *
 * 설계:
 *   - set(updater, label?) 형태로 호출. label을 넘기면 history.past에 push, 없으면 기록 생략.
 *   - api에 undo/redo/clearHistory/canUndo/canRedo 부착.
 *   - 일반 변경 시 future는 즉시 clear (classic editor semantics).
 *   - maxDepth(기본 50) 초과 시 가장 오래된 항목을 FIFO로 제거.
 */

import type { StateCreator, StoreApi } from 'zustand';
import {
  applyPatches,
  enablePatches,
  produceWithPatches,
  type Draft,
  type Patch,
} from 'immer';

enablePatches();

/** 히스토리 한 항목 */
export interface HistoryEntry {
  patches: Patch[];
  inversePatches: Patch[];
  /** UI 타임라인·라벨 예: 'updatePrintUnit', 'setAlbumInfo' */
  label: string;
  timestamp: number;
}

export interface HistoryState {
  past: HistoryEntry[];
  future: HistoryEntry[];
}

export const INITIAL_HISTORY: HistoryState = { past: [], future: [] };

/** store 상태가 반드시 포함해야 하는 history 슬롯 */
export interface WithHistory {
  history: HistoryState;
}

export interface HistoryOptions {
  /** past 최대 depth. 초과 시 가장 오래된 항목 제거 (FIFO). 기본 50. */
  maxDepth?: number;
}

/** 미들웨어 내부 set 옵션 */
export interface HistorySetOptions {
  /** true면 patches 기록 생략 (UI-only 변경 등) */
  skipHistory?: boolean;
}

/** store creator에 주입되는 set 함수 시그니처 */
export type HistorySetter<T> = (
  updater: (draft: Draft<T>) => void,
  label?: string,
  opts?: HistorySetOptions,
) => void;

/** api에 미들웨어가 부착하는 히스토리 조작 메서드 */
export interface HistoryStoreApi {
  undo: () => void;
  redo: () => void;
  clearHistory: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
}

/**
 * withImmerHistory — Zustand 미들웨어.
 *
 * 사용:
 *   create<State>()(withImmerHistory((set, get, api) => ({
 *     history: INITIAL_HISTORY,
 *     value: 0,
 *     increment: () => set((s) => { s.value += 1 }, 'increment'),
 *   }), { maxDepth: 50 }))
 */
export function withImmerHistory<T extends WithHistory>(
  config: (
    set: HistorySetter<T>,
    get: () => T,
    api: StoreApi<T> & HistoryStoreApi,
  ) => T,
  options?: HistoryOptions,
): StateCreator<T, [], [], T> {
  const maxDepth = options?.maxDepth ?? 50;

  return (set, get, api) => {
    const extApi = api as StoreApi<T> & HistoryStoreApi;

    const historySet: HistorySetter<T> = (updater, label, opts) => {
      const prev = get();
      const [next, patches, inversePatches] = produceWithPatches(
        prev,
        updater as (draft: Draft<T>) => void,
      );

      // 변경 없음
      if (next === prev) return;

      // skipHistory 또는 label 없이 호출 → 기록하지 않고 그대로 반영
      if (opts?.skipHistory || !label) {
        set(next, true);
        return;
      }

      // history.* 경로 patch는 기록에서 제외 — 무한 재귀 방지
      const filteredPatches = patches.filter((p) => p.path[0] !== 'history');
      const filteredInverse = inversePatches.filter((p) => p.path[0] !== 'history');
      if (filteredPatches.length === 0) {
        // history 슬롯만 바뀐 변경이면 항목을 만들 가치 없음
        set(next, true);
        return;
      }

      const entry: HistoryEntry = {
        patches: filteredPatches,
        inversePatches: filteredInverse,
        label,
        timestamp: Date.now(),
      };

      const prevHistory = (next as WithHistory).history;
      const nextPast = [...prevHistory.past, entry];
      const trimmed =
        nextPast.length > maxDepth ? nextPast.slice(nextPast.length - maxDepth) : nextPast;
      const mergedHistory: HistoryState = { past: trimmed, future: [] };

      const finalState = { ...(next as object), history: mergedHistory } as T;
      set(finalState, true);
    };

    extApi.canUndo = () => (get() as WithHistory).history.past.length > 0;
    extApi.canRedo = () => (get() as WithHistory).history.future.length > 0;

    extApi.undo = () => {
      const s = get() as T & WithHistory;
      const past = s.history.past;
      if (past.length === 0) return;
      const entry = past[past.length - 1];
      if (!entry) return;
      const reverted = applyPatches(s, entry.inversePatches);
      const newPast = past.slice(0, -1);
      const newFuture = [...s.history.future, entry];
      const next = {
        ...(reverted as object),
        history: { past: newPast, future: newFuture },
      } as T;
      set(next, true);
    };

    extApi.redo = () => {
      const s = get() as T & WithHistory;
      const future = s.history.future;
      if (future.length === 0) return;
      const entry = future[future.length - 1];
      if (!entry) return;
      const applied = applyPatches(s, entry.patches);
      const newFuture = future.slice(0, -1);
      const newPast = [...s.history.past, entry];
      const next = {
        ...(applied as object),
        history: { past: newPast, future: newFuture },
      } as T;
      set(next, true);
    };

    extApi.clearHistory = () => {
      const s = get();
      const next = { ...s, history: { past: [], future: [] } } as T;
      set(next, true);
    };

    return config(historySet, get as () => T, extApi);
  };
}
